import { defineStore } from "pinia";
import { ref } from "vue";
import Swal from "sweetalert2";
import { useFirestore } from "~/composables/useFirestore";
import { useRealtimeDatabase } from "~/composables/useRealtimeDatabase";
import type { RunMode } from "./pos.shop";
import { detectOfflineBoot } from "~/services/storage/offlineBuckets";
import type { BootContext, BootStrategy } from "~/services/bootstrap/types";
import { OnlineBootStrategy } from "~/services/bootstrap/onlineStrategy";
import { OfflineBootStrategy } from "~/services/bootstrap/offlineStrategy";
import { wireShopStore } from "~/services/bootstrap/registry";
import { getDevStoreOverride, handleDevStoreAuthFailure } from "~/utils/devStoreOverride";

const CASHIER_TAKEOVER_SIGNAL_KEY = "cashier_takeover_signal";

export const useBootstrapStore = defineStore("bootstrap", () => {
  const isInited = ref(false);
  const isOffline = ref(false);
  const loaded = ref(false);
  const loadingLabel = ref("");

  function redirectToLogin() {
    if (!import.meta.client) return;
    const isFileProtocol = window.location.protocol === "file:";
    const isAlreadyAtLogin = isFileProtocol
      ? window.location.hash === "#/login"
      : window.location.pathname === "/login";
    if (isAlreadyAtLogin) return;

    if (isFileProtocol) {
      window.location.hash = "#/login";
      return;
    }
    window.location.href = "/login";
  }

  function handleBootError(error: any) {
    const status = error?.status || error?.statusCode || error?.response?.status;
    const message = String(error?.message || error || "").toLowerCase();
    const isAuthError =
      status === 401 || status === 403 || message.includes("need login");
    // DEV store override: the session is synthetic, so an auth error means the
    // bypass pair was rejected — show that instead of looping through /login.
    if (isAuthError && import.meta.dev && handleDevStoreAuthFailure(status)) {
      loadingLabel.value = "DEV override: server rejected the bypass pair";
      return;
    }
    if (isAuthError) {
      // Reset only on the auth path — the login redirect reloads the page, so
      // the retry starts clean. On a non-auth failure isInited stays true:
      // Layer B may already have wired RTDB listeners, and a default-layout
      // remount re-running init() would register every onChildAdded a second
      // time (doubled acks/writes, doubled kitchen slips). The only clean
      // retry is a full page reload.
      isInited.value = false;
      loadingLabel.value = "Session expired. Redirecting to login...";
      console.log("Session expired. Redirecting to login...");
      Swal.fire({
        icon: "warning",
        title: "Session expired. Redirecting to login...",
      });
      redirectToLogin();
      return;
    }
    loadingLabel.value = "Initialization failed";
    console.log("Failed to initialize bootstrap store:", error);
    Swal.fire({
      icon: "error",
      title: "Failed to initialize bootstrap store: " + error,
    });
  }

  async function initClientDatabaseService() {
    const authStore = useAuthStore();
    if (authStore.storeId == null) throw new Error("need login");
  }

  async function initPaymentRuntime() {
    // paymentType is registered + reconciled in Layer A. Only per-cashier
    // UI state reset stays here.
    usePosPaymentStore().restart();
  }

  async function initCashierControllerRuntime() {
    // awaited: mobile ingest holds until the jobchain is loaded, because the
    // jobchain is what tells it whether an incoming order is a duplicate.
    await usePosMobileV1Store().init();
    usePosJobGatewayStore().init();
    // mobile-v2 runs alongside v1, not instead of it: the Android flag is rolled
    // out per shop, so both intakes must stay live during the migration.
    // Awaited for the same reason as v1 — its duplicate check reads the jobchain.
    await usePosMobileV2Store().init();
    usePosMobileVoidV1Store().init();
    usePosTerminalStore().init();
    usePosPrintPreviewStore().init();
  }

  async function ensureCashierAuthForRunMode() {
    const runMode = usePosShopStore().runMode;
    // Terminals too: Move / Comment / Set Price / Split Item on a terminal are
    // jobchain writes, which the RTDB rules only accept from a signed-in session
    // whose customerId claim matches the shop.
    if (runMode !== "cashier" && runMode !== "2nd_cashier" && runMode !== "terminal") return;

    // DEV store override (read-only impersonation): the synthetic session has no
    // token to mint with, and the claim belongs to the dev's real shop, so the
    // check would always bounce to /login. Skip it and leave the Firebase session
    // untouched — RTDB reads are open, and jobchain writes stay rejected by the
    // rules (claim customerId ≠ impersonated shop), which keeps it read-only.
    if (import.meta.dev && getDevStoreOverride()) {
      console.warn("[bootstrap] DEV store override — skipping cashier claim check (read-only)");
      return;
    }

    // authStore.storeId carries the CustomerId — the same value the custom-token
    // claim `customerId` must equal.
    const result = await ensureCashierFirebaseAuth(String(useAuthStore().storeId));
    if (result.status === "ok") return;

    // "mismatch" (claim customerId ≠ current customer even after re-minting) always
    // bounces — that is THE check this machine must not slip past. The other
    // failure modes stay behind the rollout flag.
    if (result.status !== "mismatch" && !STRICT_CASHIER_AUTH) {
      console.warn(`[bootstrap] cashier firebase auth not established (${result.status}) — continuing (non-strict)`);
      // Transition window: the machine must still pick up its custom-token
      // session without anyone re-logging-in. Boot-time minting covers the
      // normal case; this catches a machine that booted offline — retry
      // silently once connectivity returns.
      if (import.meta.client) {
        window.addEventListener(
          "online",
          () => {
            ensureCashierFirebaseAuth(String(useAuthStore().storeId)).then((r) =>
              console.log(`[bootstrap] cashier firebase auth retry on reconnect: ${r.status}`)
            );
          },
          { once: true }
        );
      }
      return;
    }

    if (import.meta.client && window.location.pathname !== "/login") {
      if (result.status === "unauthorized" || result.status === "mismatch") {
        localStorage.removeItem("token");
      } else {
        Swal.fire({
          icon: "error",
          title: "Cashier sign-in failed",
          text: "เครื่องแคชเชียร์ต้องต่ออินเทอร์เน็ตเพื่อลงทะเบียนครั้งแรก กรุณาเชื่อมต่อแล้ว login ใหม่",
        });
      }
      window.location.href = "/login";
      throw new Error("need login");
    }
  }

  async function initByRunMode() {
    const shopStore = usePosShopStore();
    const runMode = shopStore.runMode as RunMode;
    if (runMode == null) return;

    if (runMode === "cashier" || runMode === "2nd_cashier") {
      await initPaymentRuntime();
    }
    if (runMode === "cashier") {
      await initCashierControllerRuntime();
    }
    if (runMode === "2nd_cashier") {
      // Build only the uuid map (no receive-side) so the second cashier
      // publishes its own adds to the legacy mobile cloud — history, job
      // detail and the count list — via the global mobile-v1.sync plugin's
      // $onAction("add") hook. See initPublishOnly for why the receive-side
      // (which would double-ingest mobile orders) is excluded.
      usePosMobileV1Store().initPublishOnly();
    }
  }

  async function processRoute() {
    const authStore = useAuthStore();
    const router = useRouter();

    let seenServer = false;
    authStore.watchFirestoreOffline(({ isFirestoreOffline }) => {
      if (seenServer) {
        isOffline.value = isFirestoreOffline;
        return;
      }
      // ครั้งแรกจะได้ false มาเพราะยังติดต่อ server ยังไม่ได้
      seenServer = !isFirestoreOffline;
    });

    loaded.value = true;
    loadingLabel.value = "Loading Completed";

    setTimeout(() => {
      localStorage.setItem("firestore_loaded", "true");
    }, 5000);

    if (
      import.meta.client &&
      window.sessionStorage.getItem(CASHIER_TAKEOVER_SIGNAL_KEY)
    ) {
      return router.push("/");
    }
    // Carry the Run Mode "enter POS" flag through to /v2 (runMode.vue reloads to
    // '/?enterPos=1'; the flag survives the bootstrap-guard because it rides on '/').
    // v2/index.vue reads the query and forwards to the selling screen — the routing
    // decision stays out of bootstrap so this flow doesn't get tangled.
    if (
      import.meta.client &&
      new URLSearchParams(window.location.search).get("enterPos") === "1"
    ) {
      return router.push({ path: "/v2", query: { enterPos: "1" } });
    }
    return router.push("/v2");
  }

  async function init() {
    if (isInited.value) return;
    isInited.value = true;

    try {
      // 1. Connectivity probe BEFORE anything else. Uses RTDB
      //    `.info/connected` (global path, no storeId required). Result is
      //    cached — every downstream `isOfflineBoot()` reader sees the
      //    same answer.
      const offline = await detectOfflineBoot();
      isOffline.value = offline;
      console.log(`[bootstrap] offline boot = ${offline}`);

      const strategy: BootStrategy = offline
        ? new OfflineBootStrategy()
        : new OnlineBootStrategy();

      // const strategy: BootStrategy = new OfflineBootStrategy()


      // 2. Auth. Strategy-specific path: Firestore w/ localStorage
      //    fallback online; localStorage-only offline.
      loadingLabel.value = "Authenticating...";
      await strategy.loadAuth();

      const authStore = useAuthStore();
      if (authStore.storeId == null) throw new Error("need login");

      // 3. Wire clients now that we have a storeId. `businessMode` is a
      //    getter — settings haven't been fetched yet, so a snapshot here
      //    would always read 0. Readers (Layer B, runtime stores) pull the
      //    current value at use time after Layer A populates settings.
      const ctx: BootContext = {
        storeId: String(authStore.storeId),
        get businessMode() {
          return (
            Number(
              useSettingsStore().getSystemEnvByName?.("BusinessMode")?.Value ?? 0
            ) || 0
          );
        },
      };
      useApi().setStoreId(ctx.storeId);
      useDataservice().setCustomerId(ctx.storeId);
      useDataservice().init();
      useFirestore().setStoreId(ctx.storeId);
      useRealtimeDatabase().setStoreId(ctx.storeId);

      wireShopStore(ctx);

      // 4. Layer A (version-tracked menus/settings/etc).
      loadingLabel.value = "Loading Main Data...";
      await strategy.loadLayerA(ctx);

      // 5. shopStore.loadRunMode reads runMode from localStorage. Must run
      //    BEFORE initByRunMode reads it and BEFORE bootLayerB so the
      //    receipt/job stores see correct settings.
      await usePosShopStore().loadRunMode();

      // 5.5 Cashier Firebase Auth (RTDB security rollout). Cashier machines
      //     (main + 2nd) and terminals must hold a custom-token session whose claim customerId
      //     matches the customer, before jobchain becomes write-locked by security
      //     rules. A machine that already signed in once passes instantly from
      //     IndexedDB (works offline); otherwise we mint via
      //     POST /users/firebase-token. A claim/customer mismatch always bounces
      //     to /login; the other failure modes are fail-soft while
      //     STRICT_CASHIER_AUTH=false (Phase 1, backend not live yet).
      await ensureCashierAuthForRunMode();

      useWatchFeatureBlock().startWatching();
      usePwaCacheFlag().startWatching();

      // 6. Layer B (runtime/RTDB-backed jobs/receipts/etc). All store
      //    `loaded` flags resolve via Promise — no watch loop / timeout.
      loadingLabel.value = "Loading POS Data...";
      await strategy.bootLayerB(ctx);

      await initByRunMode();
      await processRoute();

      setTimeout(() => initClientDatabaseService(), 1000);
    } catch (error) {
      handleBootError(error);
    }
  }

  return {
    // state
    isInited,
    isOffline,
    loaded,
    loadingLabel,
    // actions
    init,
    redirectToLogin,
    handleBootError,
    initClientDatabaseService,
    initByRunMode,
    initPaymentRuntime,
    initCashierControllerRuntime,
    processRoute,
  };
});
